/**
 * MultiRoleSelector – Dropdown styled like the "User Type" Select in Create User,
 * but supports multi-selection. Click items to toggle; selected roles render as chips.
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Loader2, X, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReferenceUserRole } from '@/types/dashboardConfig';

interface MultiRoleSelectorProps {
  roles: ReferenceUserRole[];
  isLoading: boolean;
  selected: ReferenceUserRole[];
  onChange: (roles: ReferenceUserRole[]) => void;
  disabled?: boolean;
  error?: string | null;
}

export function MultiRoleSelector({
  roles,
  isLoading,
  selected,
  onChange,
  disabled,
  error,
}: MultiRoleSelectorProps) {
  const [open, setOpen] = useState(false);
  const selectedKeys = new Set(selected.map((r) => r.role));

  const toggle = (role: ReferenceUserRole) => {
    if (disabled) return;
    if (selectedKeys.has(role.role)) {
      onChange(selected.filter((r) => r.role !== role.role));
    } else {
      onChange([...selected, role]);
    }
  };

  const remove = (roleStr: string) => {
    if (disabled) return;
    onChange(selected.filter((r) => r.role !== roleStr));
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-foreground">User Type</Label>

      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled || isLoading}
            className={cn(
              'flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <div className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0">
              {isLoading ? (
                <span className="text-muted-foreground">Loading roles...</span>
              ) : selected.length === 0 ? (
                <span className="text-muted-foreground">Select user type</span>
              ) : (
                selected.map((r) => (
                  <Badge
                    key={r.role}
                    variant="secondary"
                    className="gap-1 pr-1 text-xs font-medium"
                  >
                    {r.role}
                    {!disabled && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(r.role);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            remove(r.role);
                          }
                        }}
                        className="rounded-sm opacity-60 hover:opacity-100 focus:outline-none ml-0.5 cursor-pointer"
                        aria-label={`Remove ${r.role}`}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    )}
                  </Badge>
                ))
              )}
            </div>
            {isLoading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-60" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
        >
          {roles.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">No roles available.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto py-1">
              {roles.map((r) => {
                const isSelected = selectedKeys.has(r.role);
                return (
                  <button
                    key={r.role_id}
                    type="button"
                    onClick={() => toggle(r)}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-sm text-left',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/40',
                    )}
                  >
                    <span>{r.role}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
